import React, { useContext } from 'react';
import { Box, Button, Typography, makeStyles } from '@material-ui/core';
import { Broker } from 'models';
import { useIntl } from 'react-intl';
import { images } from 'assets/images';
import { LanguageContext } from 'contexts';
import { getColorContrastRatio, getPhoneNumberWithFormatting } from 'utils';
import {
  COLOR,
  COLOR_CONTRAST_RATIO,
  isDev,
  PORTAL_CUSTOM_SECTIONS,
  ENG,
  PORTAL_SECTIONS,
} from 'config/constants';
import PortalHeader from './PortalHeader';

interface Props {
  broker: Broker;
  slogan: string;
  banner_picture_url: string;
  logo_picture_url?: string;
  portal_infos: any;
  sections?: any;
  favorite_listings: any;
  is_white_text?: boolean;
}

function BigBannerHeader(props: Props) {
  const {
    broker,
    slogan,
    banner_picture_url,
    logo_picture_url,
    portal_infos,
    sections,
    favorite_listings,
    is_white_text,
  } = props;
  const useStyles = makeStyles({
    header: {
      display: 'flex',
      position: 'relative',
      height: '100vh',
      paddingTop: 60,
      overflow: 'hidden',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 30,
    },
    bannerImage: {
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      position: 'absolute',
      objectFit: 'cover',
    },
    sloganContent: {
      zIndex: 3,
      width: '50%',
      textAlign: 'left',
      '@media (max-width: 768px)': {
        marginLeft: 20,
        width: '80%',
      },
    },
    slogan: {
      fontWeight: 'bold',
      fontSize: 30,
      color: COLOR.WHITE,
      marginBottom: 20,
      '@media (max-width: 768px)': {
        fontSize: 20,
      },
    },
    button: {
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.55)',
      },
    },
    btnText: {
      margin: 10,
      textTransform: 'initial',
      fontSize: 16,
      color: COLOR.WHITE,
      '@media (max-width: 768px)': {
        fontSize: 12,
      },
    },
    overlay: {
      position: 'absolute',
      zIndex: 2,
      bottom: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'black',
      opacity: 0.5,
    },
    stickyHeader: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: 'auto',
      backgroundColor: COLOR.WHITE,
      zIndex: 999,
    },
    buttons: {
      display: 'flex',
      marginBottom: 20,
      flexDirection: 'row',
      '@media (max-width: 768px)': {
        justifyContent: 'space-between',
      },
    },
    broker: {
      width: 'auto',
      height: 600,
      objectFit: 'cover',
      position: 'absolute',
      bottom: 0,
      right: 100,
      '@media (max-width: 800px)': {
        right: 0,
      },
      '@media (max-width: 600px)': {
        display: 'none',
      },
    },
    bannerContent: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-around',
      position: 'relative',
      width: '100%',
      height: '100%',
      zIndex: 3,
    },
    row: {
      marginBottom: 5,
      marginTop: 5,
    },
    infoText: {
      color: COLOR.WHITE,
      fontSize: 14,
      '@media (max-width: 768px)': {
        fontSize: 13,
      },
    },
  });

  const styles = useStyles();
  const intl = useIntl();
  const { language } = useContext(LanguageContext);
  const isEn = language === ENG;

  const fmtForBuyer = intl.formatMessage({ id: 'portal.forBuyer' });
  const fmtForSeller = intl.formatMessage({ id: 'portal.forSeller' });
  const primary_color = portal_infos?.template_style?.primary_color || COLOR.SECONDARY_BLUE;
  const secondary_color = portal_infos?.template_style?.secondary_color || COLOR.LIGHT_BLUE;

  const primary_color_contrast_ratio = getColorContrastRatio(primary_color, COLOR.WHITE);
  const secondary_color_contrast_ratio = getColorContrastRatio(secondary_color, COLOR.WHITE);
  const primary_bg_text_color =
    primary_color_contrast_ratio > COLOR_CONTRAST_RATIO ? COLOR.WHITE : COLOR.BLACK;
  const secondary_bg_text_color =
    secondary_color_contrast_ratio > COLOR_CONTRAST_RATIO ? COLOR.WHITE : COLOR.BLACK;
  const getSectionExists = (sectionKey: string) => {
    return (
      sections &&
      sections.find((s: any) => {
        return s.section.key === sectionKey && s.section.is_visible;
      })
    );
  };
  const getSectionUrl = (sectionKey: string) => {
    let url = `/${sectionKey}`;
    if (!isEn) {
      const section = PORTAL_CUSTOM_SECTIONS[sectionKey.toUpperCase()];
      if (section) {
        url = `/${section.key_fr}`;
      } else {
        url = `/${sectionKey}`;
      }
    }
    return isDev() ? `${url}?domain_name=${portal_infos.domain_name}` : url;
  };
  return (
    <Box>
      <Box className={styles.stickyHeader}>
        <PortalHeader
          displaySocialMedia
          logo_picture_url={logo_picture_url}
          portal_infos={portal_infos}
          broker={broker}
          sections={sections}
          favorite_listings={favorite_listings}
          is_white_text={is_white_text}
        />
      </Box>
      <Box className={styles.header}>
        <Box className={styles.overlay} />
        <img
          src={banner_picture_url || images.default_portal_banner}
          alt='house'
          className={styles.bannerImage}
        />
        <div className={styles.bannerContent}>
          <div className={styles.sloganContent}>
            <Typography className={styles.slogan}>{slogan || null}</Typography>
            <div className={styles.buttons}>
              {getSectionExists(PORTAL_CUSTOM_SECTIONS.BUYERS.key) ? (
                <Button
                  className={styles.button}
                  href={getSectionUrl(PORTAL_CUSTOM_SECTIONS.BUYERS.key)}
                  style={{
                    border: `1px solid ${secondary_color}`,
                    marginRight: 20,
                    backgroundColor: secondary_color,
                    color: secondary_bg_text_color,
                  }}
                >
                  <Typography className={styles.btnText}>{fmtForBuyer}</Typography>
                </Button>
              ) : null}
              {getSectionExists(PORTAL_CUSTOM_SECTIONS.SELLERS.key) ? (
                <Button
                  className={styles.button}
                  href={isDev() ? `/sellers?domain_name=${portal_infos.domain_name}` : '/sellers'}
                  style={{ backgroundColor: secondary_color, color: secondary_bg_text_color }}
                >
                  <Typography className={styles.btnText}>{fmtForSeller}</Typography>
                </Button>
              ) : null}
            </div>
            {((portal_infos && portal_infos.phone_number) || broker.centrisPhoneNumber) && (
              <div className={styles.row}>
                <Typography className={styles.infoText}>
                  {getPhoneNumberWithFormatting(
                    portal_infos.phone_number || broker.centrisPhoneNumber,
                  )}
                </Typography>
              </div>
            )}
            {portal_infos && portal_infos.email && (
              <div className={styles.row}>
                <Typography
                  onClick={() => {
                    window.location.href = `mailto:${portal_infos.email}`;
                  }}
                  className={styles.infoText}
                  style={{ cursor: 'pointer', fontSize: 16, fontWeight: 700 }}
                >
                  {portal_infos.email}
                </Typography>
              </div>
            )}
          </div>
          <div>
            {portal_infos.broker_image ? (
              <img src={portal_infos.broker_image} className={styles.broker} alt='broker' />
            ) : null}
          </div>
        </div>
      </Box>
    </Box>
  );
}

export default BigBannerHeader;
